// frontend/src/hooks/useTeamBigFive.js
import { useQuery } from '@tanstack/react-query';
import { talentAssessmentsAPI } from '../services/api';

const FIVE_MINUTES = 5 * 60 * 1000;

const retryFn = (failureCount, error) => {
  const s = error?.response?.status;
  if (s === 403 || s === 404) return false;
  return failureCount < 2;
};

/**
 * Directory of in-scope team members for My Team → Big Five.
 * Metadata + has_result only — scores require the audited POST.
 */
export const useTeamBigFiveDirectory = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: ['talent-assessments', 'team', 'big-five', 'directory'],
    queryFn: async () => {
      const response = await talentAssessmentsAPI.getTeamBigFiveDirectory();
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    retry: retryFn,
    enabled,
  });
};

/**
 * Department-average OCEAN benchmark for HOD / Deputy HOD — used to overlay
 * an individual's profile against their department's mean shape in the
 * My Team → Big Five result modal. Reporting Officers never fire this
 * request (`enabled` should be false for them — direct-report headcounts
 * rarely clear the k=5 anonymity gate and a department average isn't an
 * RO's remit).
 */
export const useTeamBigFiveDepartmentAggregate = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: ['talent-assessments', 'team', 'big-five', 'department-aggregate'],
    queryFn: async () => {
      const response = await talentAssessmentsAPI.getTeamBigFiveDepartmentAggregate();
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    retry: retryFn,
    enabled,
  });
};

/**
 * Lightweight Big Five status for a single team member (HOD / Deputy / RO).
 */
export const useTeamBigFiveMemberStatus = (staffId, { enabled = true } = {}) => {
  return useQuery({
    queryKey: ['talent-assessments', 'team', 'big-five', 'status', staffId],
    queryFn: async () => {
      const response = await talentAssessmentsAPI.getTeamBigFiveMemberStatus(staffId);
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    retry: retryFn,
    enabled: Boolean(staffId) && enabled,
  });
};
